import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesomeRounded,
  DescriptionOutlined,
  PictureAsPdfRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { Empty } from 'antd';
import {
  downloadAiSkillReportPdf,
  getAiSkillReportAvailability,
  getAiSkillReportHtml,
  getAiSkillReports,
  requestAiSkillReport,
} from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import ReportViewerDialog from './ReportViewerDialog';
import AiAccessStatus from '../../../common/AiAccessStatus';
import AiReportingNotProvisionedNotice from './AiReportingNotProvisionedNotice';
import ReportsAccessNotice from './ReportsAccessNotice';
import { AppContext } from '../../../../store/AppContext';
import { userCats } from '../../../../helpers/appstate';

const POLL_INTERVAL_MS = 20000;
const IN_PROGRESS = ['QUEUED', 'PROCESSING'];
const AI_COLOR = '#6a3fb5';

const reportTypeLabels = { QAQC: 'QA/QC', NORMAL: 'Consultant' };

const audienceOptions = [
  'Facility management',
  'Regulators / NESREA officers',
  'Board and executives',
  'Environmental and technical team',
  'Community stakeholders',
];

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : '-';

const getPeriod = (report) =>
  report?.reportQuarter && report?.reportYear
    ? `Q${report.reportQuarter} ${report.reportYear}`
    : report?.reportTitle || 'Report';

const getRequestedAt = (report) => report.queuedAt || report.requestedAt || report.createdAt;

const getRequestedBy = (report) =>
  report.requestedByName || report.requestedBy || report.createdByName || report.createdBy || '';

const getPdfFileName = (report) =>
  `IEMS-AI-${(report.facilityName || 'Facility').replace(/[^\w]+/g, '-')}-${getPeriod(
    report
  ).replace(' ', '-')}.pdf`;

function AiStatusChip({ report }) {
  if (IN_PROGRESS.includes(report.status)) {
    return (
      <Chip
        size="small"
        color="info"
        variant="outlined"
        icon={<CircularProgress size={12} color="inherit" />}
        label={report.status === 'QUEUED' ? 'Queued…' : 'Writing report…'}
      />
    );
  }
  if (report.status === 'FAILED') {
    return (
      <Tooltip title={report.errorMessage || ''}>
        <Chip size="small" color="error" variant="outlined" label="Couldn't generate" />
      </Tooltip>
    );
  }
  return <Chip size="small" color="success" variant="outlined" label="Ready" />;
}

export default function FacilityAiSkillReports({
  facilityId,
  yearFilter,
  reportOptions,
  refreshKey,
}) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  
  const [accessDenied, setAccessDenied] = useState(false);
  const { state } = useContext(AppContext);
  const isPersonnel = state?.auth?.result?.data?.userCategory === userCats.PERSONNEL;
  const [busy, setBusy] = useState({});
  const pollRef = useRef(null);

  // Request dialog
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestReportId, setRequestReportId] = useState('');
  const [audience, setAudience] = useState('');
  const [instructions, setInstructions] = useState('');
  const [requesting, setRequesting] = useState(false);

  // Viewer
  const [viewing, setViewing] = useState(null);
  const [html, setHtml] = useState('');
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [htmlError, setHtmlError] = useState('');

  const setBusyKey = (key, value) => setBusy((prev) => ({ ...prev, [key]: value }));

  const fetchReports = useCallback(
    async ({ silent = false } = {}) => {
      if (!facilityId) return;
      if (!silent) setLoading(true);
      try {
        const { data } = await getAiSkillReports({ facilityId, year: yearFilter });
        setReports(data);
        setAccessDenied(false);
      } catch (error) {
        if (error.accessDenied) {
          // No permission: show a notice in the tab instead of an error toast
          setReports([]);
          setAccessDenied(true);
        } else if (!silent) {
          openNotification({ type: 'error', message: error.message });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [facilityId, yearFilter]
  );

  useEffect(() => {
    fetchReports();
  }, [fetchReports, refreshKey]);

  // Availability also carries the user's AI access (on/off and requests left today)
  const refreshAvailability = useCallback(() => {
    getAiSkillReportAvailability(facilityId)
      .then(setAvailability)
      .catch(() => setAvailability({ available: false, canRequest: false }));
  }, [facilityId]);

  useEffect(() => {
    refreshAvailability();
  }, [refreshAvailability]);

  // AI reports take 3–10 minutes; keep polling while any are in progress
  useEffect(() => {
    clearTimeout(pollRef.current);
    if (reports.some((r) => IN_PROGRESS.includes(r.status))) {
      pollRef.current = setTimeout(() => fetchReports({ silent: true }), POLL_INTERVAL_MS);
    }
    return () => clearTimeout(pollRef.current);
  }, [reports, fetchReports]);

  const openRequestDialog = () => {
    setRequestReportId(reportOptions[0]?.reportId || '');
    setAudience('');
    setInstructions('');
    setRequestOpen(true);
  };

  const handleRequest = async () => {
    setRequesting(true);
    try {
      await requestAiSkillReport({
        reportId: requestReportId,
        audience: audience.trim(),
        instructions: instructions.trim(),
      });
      setRequestOpen(false);
      openNotification({
        type: 'success',
        message:
          'AI report requested. It usually takes 3–10 minutes; this list updates automatically.',
      });
      await fetchReports({ silent: true });
    } catch (error) {
      // 403 (AI off) / 429 (daily limit): the message explains it; the session is unaffected
      openNotification({ type: 'error', message: error.message });
    } finally {
      setRequesting(false);
      refreshAvailability();
    }
  };

  const loadHtml = async (id) => {
    setHtml('');
    setHtmlError('');
    setHtmlLoading(true);
    try {
      setHtml(await getAiSkillReportHtml(id));
    } catch (error) {
      setHtmlError(error.message); // 409 = not ready yet
    } finally {
      setHtmlLoading(false);
    }
  };

  const openViewer = (report) => {
    setViewing(report);
    loadHtml(report.id);
  };

  const handleDownload = async (report) => {
    const key = `pdf-${report.id}`;
    setBusyKey(key, true);
    try {
      await downloadAiSkillReportPdf(report.id, getPdfFileName(report));
    } catch (error) {
      openNotification({ type: 'error', message: error.message });
    } finally {
      setBusyKey(key, false);
    }
  };

  // Personnel can request AI reports for their facility too, unless AI is unavailable or switched off for them
  const personnelCanRequest =
    isPersonnel &&
    availability != null &&
    availability.available !== false &&
    availability.access?.allowed !== false;
  // Shown whenever the user may request AI reports. If there's no published report to base
  // one on, the dialog says so instead of offering the form.
  const canRequest = !accessDenied && (Boolean(availability?.canRequest) || personnelCanRequest);
  const hasPublishedReports = reportOptions.length > 0;

  const renderList = () => {
    if (loading) {
      return (
        <Stack spacing={2}>
          {[1, 2].map((n) => (
            <LoaderSkeleton key={n} />
          ))}
        </Stack>
      );
    }

    if (accessDenied) {
      return <ReportsAccessNotice />;
    }

    if (!reports.length) {
      return (
        <Box
          sx={{ py: 8, textAlign: 'center', bgcolor: alpha('#f4f6f8', 0.5), borderRadius: '16px' }}
        >
          <Empty
            description={
              canRequest
                ? 'No AI reports yet. Request one tailored to a specific audience or question.'
                : 'No AI reports yet.'
            }
          />
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        {reports.map((report) => {
          const isReady = report.status === 'READY';
          const pdfBusy = busy[`pdf-${report.id}`];
          return (
            <Card
              key={report.id}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                '&:hover': { borderColor: alpha(AI_COLOR, 0.4) },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha(AI_COLOR, 0.1),
                    color: AI_COLOR,
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeRounded />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {getPeriod(report)} Report
                    </Typography>
                    <Chip
                      size="small"
                      label="AI Report"
                      sx={{
                        fontWeight: 600,
                        height: 22,
                        bgcolor: alpha(AI_COLOR, 0.1),
                        color: AI_COLOR,
                      }}
                    />
                    {report.reportType && (
                      <Chip
                        size="small"
                        label={reportTypeLabels[report.reportType] || report.reportType}
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                    )}
                    {report.audience && (
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        For: {report.audience}
                      </Typography>
                    )}
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ mt: 0.75 }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <AiStatusChip report={report} />
                    <Typography variant="caption" color="text.secondary">
                      {report.completedAt
                        ? `Generated ${formatDate(report.completedAt)}`
                        : `Requested ${formatDate(getRequestedAt(report))}`}
                    </Typography>
                  </Stack>
                  {report.instructions && (
                    <Tooltip title={report.instructions}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontStyle: 'italic',
                        }}
                      >
                        “{report.instructions}”
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              </Stack>
              {isReady && (
                <Stack direction="row" spacing={1} flexShrink={0}>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    startIcon={<VisibilityRounded />}
                    onClick={() => openViewer(report)}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={pdfBusy ? <CircularProgress size={14} /> : <PictureAsPdfRounded />}
                    disabled={pdfBusy}
                    onClick={() => handleDownload(report)}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}
                  >
                    {pdfBusy ? 'Preparing PDF…' : 'Download PDF'}
                  </Button>
                </Stack>
              )}
            </Card>
          );
        })}
      </Stack>
    );
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        {/* <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
          Tailored versions of the published report, written for a particular reader or question
          from the same IEMS figures. They are supplementary; the standard report remains the
          official record.
        </Typography> */}
        {canRequest && (
          <Button
            variant="contained"
            disableElevation
            startIcon={<AutoAwesomeRounded />}
            onClick={openRequestDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              bgcolor: AI_COLOR,
              flexShrink: 0,
              '&:hover': { bgcolor: '#55309a' },
            }}
          >
            Request AI report
          </Button>
        )}
      </Stack>

      {availability?.access?.allowed === false &&
      availability.access.reasonCode === 'DISABLED' ? (
        // AI reporting is switched off for this user/facility: explain and point to the admin
        <AiReportingNotProvisionedNotice reason={availability.access.reason} />
      ) : availability?.access && availability.access.allowed === false ? (
        <AiAccessStatus access={availability.access} unit="AI reports" sx={{ mb: 2 }} />
      ) : (
        <>
          {availability && !availability.available && availability.reason && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
              {availability.reason}
            </Alert>
          )}
          {canRequest && (
            <AiAccessStatus
              access={availability?.access}
              unit="AI reports"
              color={AI_COLOR}
              sx={{ mb: 2, maxWidth: 420 }}
            />
          )}
        </>
      )}

      {renderList()}

      {/* Request dialog */}
      <Dialog
        open={requestOpen}
        onClose={() => !requesting && setRequestOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Request an AI report</DialogTitle>
        <DialogContent>
          {!hasPublishedReports ? (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(AI_COLOR, 0.1),
                  color: AI_COLOR,
                }}
              >
                <DescriptionOutlined />
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                No published report{yearFilter ? ` for ${yearFilter}` : ''} yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                An AI report is written from a published quarterly monitoring report, and this
                facility doesn't have one{yearFilter ? ` for ${yearFilter}` : ''} yet. You can
                request an AI report once a quarterly report has been published.
                {yearFilter ? ' You can also set the Year filter to All to include other years.' : ''}
              </Typography>
            </Stack>
          ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Published report</InputLabel>
              <Select
                label="Published report"
                value={requestReportId}
                onChange={(e) => setRequestReportId(e.target.value)}
              >
                {reportOptions.map((option) => (
                  <MenuItem key={option.reportId} value={option.reportId}>
                    {getPeriod(option)}
                    {option.reportType
                      ? ` · ${reportTypeLabels[option.reportType] || option.reportType}`
                      : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* <Autocomplete
              freeSolo
              options={audienceOptions}
              inputValue={audience}
              onInputChange={(_, value) => setAudience(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Audience (optional)"
                  placeholder="Who will read this report?"
                />
              )}
            /> */}
            <TextField
              label="Instructions (optional)"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              placeholder="e.g. Focus on the effluent exceedances and what the facility should fix first."
              inputProps={{ maxLength: 2000 }}
            />
            <AiAccessStatus access={availability?.access} unit="AI reports" color={AI_COLOR} />
            <Alert severity="info" sx={{ borderRadius: '12px' }}>
              The report takes 3–10 minutes to prepare. It will appear in this list and update
              automatically when it's ready.
            </Alert>
          </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setRequestOpen(false)}
            disabled={requesting}
            sx={{ textTransform: 'none' }}
          >
            {hasPublishedReports ? 'Cancel' : 'Close'}
          </Button>
          {hasPublishedReports && (
          <Button
            variant="contained"
            disableElevation
            onClick={handleRequest}
            disabled={requesting || !requestReportId}
            startIcon={
              requesting ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeRounded />
            }
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: AI_COLOR,
              '&:hover': { bgcolor: '#55309a' },
            }}
          >
            {requesting ? 'Requesting…' : 'Request report'}
          </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Viewer */}
      <ReportViewerDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={
          viewing
            ? `${viewing.facilityName ? `${viewing.facilityName} — ` : ''}${getPeriod(
                viewing
              )} (AI-report)`
            : ''
        }
        subtitle={
          viewing?.completedAt
            ? `Generated ${formatDate(viewing.completedAt)}${
                viewing.audience ? ` · For ${viewing.audience}` : ''
              }`
            : ''
        }
        banner={
          <Alert
            severity="warning"
            icon={<AutoAwesomeRounded fontSize="small" />}
            sx={{ borderRadius: 0 }}
          >
            AI-generated report — supplementary only. The standard report is the official record.
          </Alert>
        }
        html={html}
        loading={htmlLoading}
        error={htmlError}
        onRetry={() => viewing && loadHtml(viewing.id)}
        onDownload={() => viewing && handleDownload(viewing)}
        downloading={viewing ? busy[`pdf-${viewing.id}`] : false}
      />
    </Box>
  );
}
